import React, { useState, useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import { createStyles, makeStyles } from '@material-ui/core';
import AddEditHeader from '../../ui/admin/AddEditHeader';
import ArticleEditor from '../../ui/admin/ArticleEditor';
import ArticleSideBar from '../../ui/admin/ArticleSideBar';
import {
	ArticleChangeHookType,
	ArticleEditError,
	ArticleState,
	ResearchRole
} from '../../../shared/global-enums';
import {
	AlertMessage,
	Article,
	ArticleInfo,
	ArticleUpsertDto,
	CategoryItemNode, Channel, ChannelOwner, ChannelTypes, Desk, IdAndName, PromotionalContentSummary,
	Template, User
} from '../../../shared/types';
import {
	AlertBox,
	Checkbox,
	ExtendedTheme,
	Loader,
	Option,
	TextBlock
} from 'neon-design-system';
import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import { useNotification } from '../../providers/NotificationProvider';
import { useUser } from '../../providers/UserProvider';
import {
	FormSnackBarType,
	generateSnackBarFormMessage,
	useFormControl,
	useFormGroup
} from '../../../utils/form.utils';
import {
	ArticleValidationError,
	dateValidator,
	requiredValidator
} from '../../../utils/validator.utils';
import {
	ADD_ARTICLES,
	ALL_STATIC,
	ARCHIVE_ARTICLE,
	EDIT_ARTICLE,
	GET_ARTICLE,
	GET_SUBSCRIBERS_COUNT,
	GET_TEMPLATE,
	PUBLISH_ADD,
	PUBLISH_EDIT
} from '../../../gql/articles';
import { DESKS_SHORT } from '../../../gql/desks';
import { TABLEAU_ENABLED } from '../../../utils/tableau';
import {
	getCategoriesAsList,
	prepareStaticDataList
} from '../../../utils/list-utils';
import { convertToDate } from '../../../utils/date.utils';
import { ManageTemplateInstance } from '../../ui/ManageTemplate';
import {
	getContributorRoleUsers,
	getUsersWithNames
} from '../../../utils/filtering.utils';
import ManageTemplateDialog from '../../ui/ManageTemplateDialog';
import { translateErrorMessage } from '../../../shared/error-handling';
import {
	getPromotionalContentIdsFromHtml,
	htmlToPromotionalContentShortcodes, promotionalContentShortcodesToHtml
} from '../../../utils/promotional-content.utils'
import { baseUrls } from '../../../utils/baseUrls.utils';
import { USER_PUBLISH_CHANNELS } from '../../../gql/users';
import { GET_CHANNELS_ADD_EDIT_ARTICLE } from '../../../gql/channels';
import { config } from '../../../config';
import { usersContains, isStringArray } from '../../../utils/utils';
import { isEqual } from 'lodash';

const returnListUrl = baseUrls.publisher.articles.list;

enum DialogType {
	UpdateArticleWithoutSubscribers,
	UpdateArticle,
	PublishArticleWithoutSubscribers,
	PublishArticle,
	PublishArticleWithBackDateAndWithoutSubscribers,
	PublishArticleWithBackDate,
}

const useStyles = makeStyles((theme: ExtendedTheme) => createStyles({
	container: {
		display: 'flex',
		width: '100%',
		height: '100%',
		paddingLeft: theme.spacing(2)
	},
	editContainer: {
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
		paddingBottom: theme.spacing(6)
	},
	dialogCaption: {
		display: 'flex'
	},
	dialogCheckbox: {
		marginTop: theme.spacing(2)
	},
	rightSideBarContainer: {
		backgroundColor: theme.palette.grey[800],
		marginLeft: theme.spacing(2),
		minWidth: 368,
		width: 368,
		display: 'flex',
		flexDirection: 'column'
	},
	editContent: {
		maxWidth: 1200,
		width: '100%'
	},
	alert: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2)
	},
	form: {
		height: '100%'
	}
}));

interface ErrorPair {
	value: string | any;
	kind: ArticleEditError;
}

interface DialogLabel {
	notification: string;
	confirmation: string;
}

const getLabelAccordingToDialogType = (dialogType: DialogType): DialogLabel => {
	switch (dialogType) {
	case DialogType.PublishArticle:
		return {
			notification: ' All subscribers will be notified.',
			confirmation: ' Do you want to proceed?'
		};
	case DialogType.PublishArticleWithBackDate:
		return {
			notification: ' This article will be available to all readers.',
			confirmation: ' Before publishing, check below if you want to notify all subscribers.'
		};
	case DialogType.UpdateArticle:
		return {
			notification: '',
			confirmation: ' Before submitting it, check below if you want to notify all the subscribers of this update.'
		};
	case DialogType.PublishArticleWithoutSubscribers:
	case DialogType.UpdateArticleWithoutSubscribers:
	case DialogType.PublishArticleWithBackDateAndWithoutSubscribers:
		return {
			notification: '',
			confirmation: ' Do you want to proceed?'
		};

	default:
		return {
			notification: '',
			confirmation: ''
		};
	}
}

const getDialogType = (
	action: string,
	numberOfSubscribers: number,
	withCustomPublishDate: boolean
): DialogType => {
	if (action === 'Publish' && numberOfSubscribers > 0 && !withCustomPublishDate)
		return DialogType.PublishArticle;

	if (action === 'Publish' && numberOfSubscribers > 0 && withCustomPublishDate)
		return DialogType.PublishArticleWithBackDate;

	if (action === 'Publish' && numberOfSubscribers === 0 && !withCustomPublishDate)
		return DialogType.PublishArticleWithoutSubscribers;

	if (action === 'Publish' && numberOfSubscribers === 0 && withCustomPublishDate)
		return DialogType.PublishArticleWithBackDateAndWithoutSubscribers;

	if (action !== 'Publish' && numberOfSubscribers > 0)
		return DialogType.UpdateArticle;

	if (action !== 'Publish' && numberOfSubscribers === 0)
		return DialogType.UpdateArticleWithoutSubscribers;

	return DialogType.PublishArticle;
}

const formatIsPremiumValue = (value: boolean | undefined) => {
	if (typeof value === undefined) return undefined;

	return !value;
}

export const ArticleAddEdit = () => {
	const classes = useStyles();
	const { _id, templateId } = useParams<{ _id: string; templateId: string }>();
	const history = useHistory();
	const { userDetails, hasScope } = useUser();
	const isAdmin = [ResearchRole.Administrator].some(hasScope);
	const {data: tableauData} = useQuery(TABLEAU_ENABLED, {fetchPolicy: 'network-only'});
	const userHasTableuAccess = tableauData?.me?.tableauAccess;

	const publisherFormControl = useFormControl<string>('');
	const contributorsFormControl = useFormControl<string[]>([]);
	const detailSummaryFormControl = useFormControl<string>('');
	const categoriesFormControl = useFormControl<string[]>([]);
	const tagsFormControl = useFormControl<string[]>([]);
	const publishDateFormControl = useFormControl<Date | undefined>(undefined);
	const articleTextFormControl = useFormControl<string | undefined>(undefined);
	const articleTitleFormControl = useFormControl<string>('', [requiredValidator]);
	const openToAllFormControl = useFormControl<boolean>(false, []);
	const expiryOffsetFormControl = useFormControl<string>('');
	const channelFormControl = useFormControl<string>('', []);
	const showWatermarkFormControl = useFormControl<boolean>(false, []);
	const displayInChartSectionFormControl = useFormControl<boolean>(false, []);
	const isPinnedFormControl = useFormControl<boolean>(false, []);
	const isTimelessFormControl = useFormControl<boolean>(false, []);

	const setInitialFormValues = (values: Article | Template | ArticleUpsertDto) => {
		articleTitleFormControl.setInitialValue(values.title);
		articleTextFormControl.setInitialValue(values.html || '');
		publisherFormControl.setInitialValue(values.publishedBy?._id);
		detailSummaryFormControl.setInitialValue('summary' in values ? values.summary : '');
		publishDateFormControl.setInitialValue('publishDate' in values ? values.publishDate : '');
		openToAllFormControl.setInitialValue('isPremium' in values ? formatIsPremiumValue(values.isPremium) : undefined);
		expiryOffsetFormControl.setInitialValue('premiumExpiryOffset' in values ? values.premiumExpiryOffset : '');
		channelFormControl.setInitialValue('premiumChannel' in values ? values.premiumChannel : '');
		contributorsFormControl.setInitialValue(isStringArray(values.contributors) ?
			values.contributors : ((values.contributors || []) as Partial<User>[]).map(c => c._id));
		categoriesFormControl.setInitialValue(isStringArray(values.categories) ?
			values.categories : ((values.categories || []) as IdAndName[]).map(c => c._id));
		tagsFormControl.setInitialValue(isStringArray(values.tags) ?
			values.tags : ((values.tags || []) as IdAndName[]).map(c => c._id));
		showWatermarkFormControl.setInitialValue('showWatermark' in values ? values.showWatermark : false);
		displayInChartSectionFormControl.setInitialValue('displayInChartSection' in values ? values.displayInChartSection : false);
		isPinnedFormControl.setInitialValue('isPinned' in values ? values.isPinned : false);
		isTimelessFormControl.setInitialValue('isTimeless' in values ? values.isTimeless : false);
	}

	const [getChannels, { loading: channelsLoading, data: channelsData }] = useLazyQuery(USER_PUBLISH_CHANNELS, {
		fetchPolicy: 'network-only'
	});

	const { loading: desksLoading, data: desksData } = useQuery(DESKS_SHORT, {
		fetchPolicy: 'network-only',
		variables: {
			getOnlyUsersDesks: true
		}
	});
	const { loading: allChannelsLoading, data: allChannelsData } = useQuery(GET_CHANNELS_ADD_EDIT_ARTICLE, { fetchPolicy: 'network-only' });

	const { data, loading } = useQuery(GET_ARTICLE, {
		skip: !!templateId || !_id,
		variables: { id: _id },
		fetchPolicy: 'network-only'
	})

	const templateQuery = useQuery(GET_TEMPLATE, {
		skip: !templateId,
		variables: { id: templateId },
		fetchPolicy: 'network-only',
		onCompleted: (data) => {
			const template: Template = data.document;
			if (!template) return;

			setInitialFormValues(template);

			const currentDate = moment().format('DD MMM YYYY');
			const formattedTitle = template.title.replace(/{{Today}}/g, currentDate);
			articleTitleFormControl.setInitialValue(formattedTitle);
		}
	});

	const { data: staticData, loading: staticLoading } = useQuery(ALL_STATIC, { fetchPolicy: 'network-only' });

	const querySubscribers = useQuery(GET_SUBSCRIBERS_COUNT, {
		fetchPolicy: 'network-only',
		variables: {
			channelId: channelFormControl.value ? channelFormControl.value : undefined
		}
	});

	const { showMessage } = useNotification();
	const [addDbAction] = useMutation(ADD_ARTICLES);
	const [editDbAction] = useMutation(EDIT_ARTICLE);
	const [archiveDbAction] = useMutation(ARCHIVE_ARTICLE);
	const [publishAddDbAction] = useMutation(PUBLISH_ADD);
	const [publishEditDbAction] = useMutation(PUBLISH_EDIT);

	const [formBusy, setFormBusy] = useState<boolean>(false);
	const [confirmationDialogOpened, setConfirmationDialogOpened] = useState<boolean>(false);
	const [publishDialogOpened, setPublishDialogOpened] = useState<boolean>(false);
	const [pageMessage, setPageMessage] = useState<AlertMessage>({ style: 'info', message: null });

	let busyTimeout: NodeJS.Timeout;
	const [isDraftInLocalStorage, setIsDraftInLocalStorage] = useState<boolean>(false);

	const draftHandlers = !templateId && isDraftInLocalStorage ? {
		onResume: () => {
			const draftArticleDataFromLocalStorage = localStorage?.getItem('articleDraft');
			const draftArticleData: ArticleUpsertDto | null = draftArticleDataFromLocalStorage ?
				JSON.parse(draftArticleDataFromLocalStorage || '') : null

			if (draftArticleData) {
				setInitialFormValues(draftArticleData);
				setIsDraftInLocalStorage(false);
			}
		},
		onDiscard: () => {
			localStorage.removeItem('articleDraft');
			setIsDraftInLocalStorage(false);
		}
	} : undefined;


	const form = useFormGroup({
		publisher: publisherFormControl,
		contributors: contributorsFormControl,
		detailSummary: detailSummaryFormControl,
		categories: categoriesFormControl,
		tags: tagsFormControl,
		articleText: articleTextFormControl,
		articleTitle: articleTitleFormControl,
		publishDate: publishDateFormControl,
		openToAll: openToAllFormControl,
		expiryOffset: expiryOffsetFormControl,
		channel: channelFormControl,
		showWatermark: showWatermarkFormControl,
		displayInChartSection: displayInChartSectionFormControl,
		isPinned: isPinnedFormControl,
		isTimeless: isTimelessFormControl
	});

	const editMode = !!data?.document;

	const articleInfo: ArticleInfo = {
		articleState: editMode ? (data?.document.state) : undefined,
		createDate: convertToDate(data?.document?.created),
		updateDate: new Date(data?.document?.lastEdited)
	};

	const [listUsers, setListUsers] = useState<User[]>([]);
	const listTags = prepareStaticDataList(staticData?.tags);
	const listPromotionalContent: PromotionalContentSummary[] = [...(staticData?.promotionalContent ?? [])];
	const userChannels = channelsData?.me?.channelsToPublish || [];
	const adminChannels = allChannelsData?.channels || [];
	const preparedChannelsList = isAdmin ? [...adminChannels] : [...userChannels];

	const selectedChannel: Channel | undefined = (preparedChannelsList || []).find(x => x._id === form.channel.value);

	const wasPublishedPreviously = editMode && data.document.wasPublishedPreviously;

	const goBack = () => {
		history.push(returnListUrl);
	}

	function onChangeSidebarValue(changeType: ArticleChangeHookType, value: string | string[] | boolean) {
		switch (changeType) {
		case ArticleChangeHookType.DetailsPublished:
			publisherFormControl.setValue(value);
			break;
		case ArticleChangeHookType.DetailsContributor:
			contributorsFormControl.setValue(value);
			break;
		case ArticleChangeHookType.DetailsSummary:
			detailSummaryFormControl.setValue(value);
			break;
		case ArticleChangeHookType.DetailsPublishDate:
			publishDateFormControl.setValue(value);
			break;
		case ArticleChangeHookType.DetailsOpenToAll:
			openToAllFormControl.setValue(value);
			expiryOffsetFormControl.setValue(undefined);
			break;
		case ArticleChangeHookType.DetailsShowWatermark:
			showWatermarkFormControl.setValue(value);
			break;
		case ArticleChangeHookType.DetailsDisplayInChartSection:
			displayInChartSectionFormControl.setValue(value);
			break;
		case ArticleChangeHookType.DetailsIsPinned:
			isPinnedFormControl.setValue(value);
			break;
		case ArticleChangeHookType.DetailsIsTimeless:
			isTimelessFormControl.setValue(value);
			break;
		case ArticleChangeHookType.DetailsChannel:
			channelFormControl.setValue(value);
			openToAllFormControl.setValue(undefined);
			expiryOffsetFormControl.setValue('');
			contributorsFormControl.setValue([]);
			publisherFormControl.setValue(preparedChannelsList.find((c: Channel) => c._id === value)?.ownerId);
			break;
		case ArticleChangeHookType.DetailsPremiumOffset:
			expiryOffsetFormControl.setValue(value);
			break;
		case ArticleChangeHookType.TaxonomySection:
			categoriesFormControl.setValue([]);
			break;
		default:
			throw new Error(`UpdateType ${changeType} is not supported`);
		}
	}

	function onChangeMultiValue(changeType: ArticleChangeHookType, value: Option[]) {

		const newValues = (value || []).map(x => x.value.toString());

		switch (changeType) {
		case ArticleChangeHookType.TaxonomyCategories:
			categoriesFormControl.setValue(newValues);
			break;
		case ArticleChangeHookType.TaxonomyTags:
			tagsFormControl.setValue(newValues);
			break;
		default:
			throw new Error(`UpdateType ${changeType} is not supported`);
		}
	}

	const setBusy = (busy: boolean) => {
		clearTimeout(busyTimeout);
		if (!busy) {
			setFormBusy(false);
			return;
		}

		busyTimeout = setTimeout(() => setFormBusy(true), 2000);
	}

	async function onSubmit(event: any) {
		changeValidators(false);
		event.preventDefault();
		form.setDirty();

		if (!form.getIsValid()) {
			return;
		}

		await saveArticle(generateArticleDto());
	}

	function validateBeforePublish(): ArticleValidationError[] {

		const fieldsToCheck: ErrorPair[] = [
			{ value: form.articleText, kind: ArticleEditError.TextAbsent },
			{ value: form.publisher, kind: ArticleEditError.PublisherAbsent },
			{ value: form.contributors, kind: ArticleEditError.ContributorAbsent },
			{ value: form.detailSummary, kind: ArticleEditError.SummaryAbsent },
			{ value: form.categories, kind: ArticleEditError.CategoriesAbsent },
			{ value: form.articleTitle, kind: ArticleEditError.TitleAbsent },
			{ value: form.channel, kind: ArticleEditError.ChannelAbsent }
		];

		return fieldsToCheck.map((item) => ({
			errorType: item.kind,
			message: (item as any).value.validationMessage
		}));
	}

	function handleValidation() {
		form.articleTitle.setDirty(true);
		changeValidators(true);
	}

	async function onPublish(withEmail?: boolean) {
		handleValidation();
		if (!form.getIsValid()) {
			return;
		}

		await publishArticle(generateArticleDto(withEmail));
	}

	function changeValidators(isPublish: boolean) {
		const newValidators = isPublish ? [requiredValidator] : [];

		form.articleText.setValidators(newValidators);
		form.publisher.setValidators(newValidators);
		form.contributors.setValidators(newValidators);
		form.detailSummary.setValidators(newValidators);
		form.categories.setValidators(newValidators);
		form.publishDate.setValidators([dateValidator]);
		form.channel.setValidators(newValidators);
	}

	function deleteArticle() {
		setPageMessage({ style: 'info', message: 'deleted' });
	}

	const saveArticle = async (item: any): Promise<void> => {
		try {
			setBusy(true);

			editMode ?
				await editDbAction({ variables: { item } }) :
				await addDbAction({ variables: { item } });

			showMessage(
				generateSnackBarFormMessage(
					ManageTemplateInstance.Article,
					form.articleTitle.value,
					editMode ? FormSnackBarType.Updated : FormSnackBarType.Added
				)
			);
			localStorage.removeItem('articleDraft');
			goBack();
		} catch (error) {
			console.error(error.message);
			setBusy(false);
			setPageMessage({
				style: 'error',
				message: translateErrorMessage(error.message)
			});
		}
	}

	const publishArticle = async (item: any): Promise<void> => {
		try {
			setBusy(true);

			editMode ?
				await publishEditDbAction({ variables: { item } }) :
				await publishAddDbAction({ variables: { item } });

			showMessage(
				generateSnackBarFormMessage(
					ManageTemplateInstance.Article,
					form.articleTitle.value,
					FormSnackBarType.Published
				)
			);
			localStorage.removeItem('articleDraft');
			goBack();

		} catch (error) {
			console.error(error.message);
			setBusy(false);
			setPageMessage({
				style: 'error',
				message: translateErrorMessage(error.message)
			});
		}
	}

	const archiveArticle = async (): Promise<void> => {
		if (!editMode) {
			return;
		}
		try {
			setBusy(true);

			await archiveDbAction({
				variables: {
					item: {
						_id,
						version: data?.document?.version
					}
				}
			});

			showMessage(
				generateSnackBarFormMessage(
					ManageTemplateInstance.Article,
					form.articleTitle.value,
					FormSnackBarType.Archived
				)
			);
			localStorage.removeItem('articleDraft');
			goBack();

		} catch (error) {
			console.error(error.message);
			setBusy(false);
			setPageMessage({
				style: 'error',
				message: translateErrorMessage(error.message)
			});
		}
	}

	const isNotFreeOpenChannel = selectedChannel && selectedChannel.type !== ChannelTypes.freeOpen;

	const generateArticleDto = (withEmail?: boolean) => {
		// promotionalContent parser
		const html = htmlToPromotionalContentShortcodes(form.articleText.value || '');
		return {
			_id,
			publishedBy: form.publisher.value ? {
				_id: form.publisher.value,
				type: (preparedChannelsList.find((c: any) => c._id === form.channel.value)?.ownerType || 'desk') as ChannelOwner
			} : null,
			contributors: form.contributors.value ?? [],
			author: userDetails?.id,
			title: form.articleTitle.value || undefined,
			html: html,
			summary: form.detailSummary.value || undefined,
			categories: form.categories.value ? form.categories.value : [],
			tags: form.tags.value ? form.tags.value : [],
			state: ArticleState[ArticleState.Pending],
			version: data?.document?.version,
			sendEmails: withEmail,
			publishDate: form.publishDate.value || undefined,
			templateId: data?.document?.templateId || templateQuery?.data?.document._id,
			isPremium: isNotFreeOpenChannel ? formatIsPremiumValue(form.openToAll.value) : undefined,
			promotionalContentIds: getPromotionalContentIdsFromHtml(html),
			premiumExpiryOffset: form.expiryOffset.value || '',
			premiumChannel: form.channel.value ? form.channel.value : null,
			showWatermark: form.showWatermark.value,
			displayInChartSection: form.displayInChartSection.value,
			isPinned: form.isPinned.value,
			isTimeless: form.isTimeless.value
		} as ArticleUpsertDto;
	}

	useEffect(() => {
		const autoSaveBanner = localStorage?.getItem('autoSaveMessage');
		const isAutoSaveBannerHidden = autoSaveBanner ? Boolean(autoSaveBanner) : false;
		if(isAutoSaveBannerHidden) return;

		setPageMessage({
			style: 'info',
			message: <><b>Insights will auto save your content as you type.</b> If you have any issues with this screen, you can go back and resume editing.</>,
			id: 'autoSaveMessage'
		})
	}, [])

	useEffect(() => {
		const draftArticleDataFromLocalStorage = localStorage?.getItem('articleDraft');
		const draftArticleData: ArticleUpsertDto | null = draftArticleDataFromLocalStorage ?
			JSON.parse(draftArticleDataFromLocalStorage || '') : null

		const articleData = generateArticleDto();
		if (draftArticleData && !isEqual(articleData, draftArticleData) && articleData?._id === draftArticleData?._id) {
			setIsDraftInLocalStorage(true)
		}
	}, [])

	useEffect(() => {
		const intervalID = setTimeout(() =>  {
			const articleData = generateArticleDto();
			if (form.isChanged) localStorage.setItem('articleDraft', JSON.stringify(articleData));
		}, config.editorAutoSaveInterval);

		return () => clearInterval(intervalID);
	}, [form.isChanged, generateArticleDto]);

	useEffect(() => {
		getChannels();
	}, [getChannels])

	useEffect(() => {
		if (data?.document && staticData?.promotionalContent) {
			const article: Article = data.document;
			// promotionalContent parser
			article.html = promotionalContentShortcodesToHtml(article.html, staticData.promotionalContent);
			if (article) setInitialFormValues(article);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, staticData])

	useEffect(() => {
		let users: User[] = [];

		if (desksData) {
			if (isAdmin || channelFormControl.value) {
				const currentChannel = preparedChannelsList.find((i: Channel) => i._id === channelFormControl.value);

				if (currentChannel && currentChannel.ownerType === 'desk') {
					const currentDesk = staticData?.desks.find((i: Desk) => i._id === currentChannel.ownerId);

					users = getContributorRoleUsers(getUsersWithNames(currentDesk?.contributors));
				} else {
					users = getContributorRoleUsers(getUsersWithNames(staticData?.users));
				}
			}
		}

		setListUsers(users);
	}, [channelFormControl.value, publisherFormControl.value, staticData, desksData])

	useEffect(() => {
		if (!data || !allChannelsData || !desksData) return;
		// close this page for not admins, not contributors and not desk contributors

		const desksContributors = data.document.publishedBy?.type === 'desk' ?
			desksData?.documents.find((d: Desk) => d._id === data.document.publishedBy?._id)?.contributors : [];

		const channelAdmins = data.document.premiumChannel
			? allChannelsData?.channels?.find((c: Channel) => c._id === data.document.premiumChannel)?.adminUsers
			: [];

		if (!(
			isAdmin ||
			usersContains(channelAdmins, userDetails?.id || '') ||
			usersContains(data.document.contributors || [], userDetails?.id || '') ||
			usersContains(desksContributors, userDetails?.id || '')
		)) {
			history.push(baseUrls.publisher.articles.list);
		}
	}, [data, allChannelsData, desksData])

	if (loading || staticLoading || templateQuery.loading || channelsLoading || desksLoading || allChannelsLoading) return <Loader />;

	const subscribersCount = querySubscribers.data?.getSubscribersCount?.count ?? 0;

	const treeCategories = (staticData?.categoriesAsTree as CategoryItemNode[]) ?? [];
	const categoryList = getCategoriesAsList(treeCategories, []);
	const categories = form.categories.value.map(
		(categoryId) => categoryList.find(
			(category: any) => category._id === categoryId)
	);
	const categoriesToString = categories.map(category => category?.name).join(', ');
	const errorList = validateBeforePublish();
	const textAbsentError = errorList.find(x => x.errorType === ArticleEditError.TextAbsent)?.message;
	const headerTitleError = (form.articleTitle.showError ? form.articleTitle.validationMessage : '');

	return (
		<form onSubmit={onSubmit} className={classes.form}>
			<div className={classes.container}>
				<div className={classes.editContainer}>
					<div className={classes.editContent}>
						<AddEditHeader
							isNew={!editMode}
							editableHeader={true}
							editablePlaceholder="Type to edit the title..."
							entityName="article"
							pathArea="Articles"
							returnUrl={returnListUrl}
							value={form.articleTitle.value}
							errorMessage={headerTitleError}
							onChange={(articleTitle: string) => {
								const articleTitleWithoutLineBreaks = articleTitle.replace(/(\r\n|\n|\r)/gm, ' ');
								form.articleTitle.setValue(articleTitleWithoutLineBreaks);
							}}
							isResumeMessageVisible={draftHandlers}
						/>
						<div className={classes.alert}>
							<AlertBox
								id="article-add-edit-alert"
								visible={!!pageMessage.message}
								children={pageMessage.message}
								styleName={pageMessage.style}
								onClose={() => {
									if (pageMessage.id === 'autoSaveMessage') {
										localStorage.setItem('autoSaveMessage', 'true');
									}
									setPageMessage({ style: 'info', message: null })
								}}
							/>
						</div>
						{
							// Froala bug. Can't send multiple updates in quick succession.
							// See https://github.com/froala/react-froala-wysiwyg/issues/221#issuecomment-605609436
						}
						{(form.articleText.value !== undefined || (!editMode && !templateId)) &&
							<ArticleEditor
								initialHtml={form.articleText.value ?? ' '}
								errorMessage={textAbsentError}
								onChange={(html) => {
									form.articleText.setValue(html);
								}}
								promotionalContent={listPromotionalContent?.sort((a: any, b: any) =>
									a.name.toLowerCase().localeCompare(b.name.toLowerCase())
								) || []}
								setPageMessage={setPageMessage}
							/>}
					</div>
				</div>

				<div className={classes.rightSideBarContainer}>
					<ArticleSideBar
						isNew={!editMode}
						selectedContributors={form.contributors.value}
						detailSummary={form.detailSummary.value}
						publishDate={form.publishDate.value}
						listUsers={listUsers}
						treeCategories={treeCategories}
						listTags={listTags}
						preparedChannelsList={preparedChannelsList}
						selectedCategories={form.categories.value}
						selectedTags={form.tags.value}
						openToAll={form.openToAll.value}
						expiryOffset={form.expiryOffset.value ?? ''}
						selectedChannel={selectedChannel}
						showWatermark={form.showWatermark.value}
						displayInChartSection={form.displayInChartSection.value}
						isPinned={form.isPinned.value}
						isTimeless={form.isTimeless.value}
						userHasTableuAccess={userHasTableuAccess}
						formBusy={formBusy}
						articleInfo={articleInfo}
						errorList={errorList}
						archiveArticle={() => setConfirmationDialogOpened(true)}
						publishArticle={() => {
							handleValidation();
							if (!form.getIsValid()) {
								return;
							}

							setPublishDialogOpened(true)
						}}
						deleteDraft={deleteArticle}
						onChangeValue={onChangeSidebarValue}
						onChangeMultiValue={onChangeMultiValue} />

					<ManageTemplateDialog
						title="Archive this article?"
						dialogMessage={<>You are about to archive the article
							<strong> {form.articleTitle?.value}</strong>.
							Archived articles are removed from publication so you must be sure and confirm.</>}
						submitBtnText="Archive article"
						dialogOpenState={confirmationDialogOpened}
						onDelete={archiveArticle}
						submitBtnColorType="error"
						closeDialog={() => setConfirmationDialogOpened(false)}
					/>

					<PublishDialog
						dialogOpenState={publishDialogOpened}
						articleTitle={form.articleTitle?.value}
						onPublish={onPublish}
						onCloseDialog={() => setPublishDialogOpened(false)}
						articleState={data?.document?.state}
						wasPublishedPreviously={wasPublishedPreviously}
						isCustomPublishDate={!!form.publishDate.value}
						categoriesAsString={categoriesToString}
						numberOfSubscribers={subscribersCount}
					/>

				</div>

			</div>
		</form>
	);
};

const usePublishDialogStyles = makeStyles((theme: ExtendedTheme) => createStyles({
	dialogCheckbox: {
		marginTop: theme.spacing(2),
		'& > label': {
			marginBottom: 0,
			'& > span > span': {
				color: '#B3B3B3'
			}
		}
	},
	numberOfCategorySubscriptionsBlock: {
		background: theme.palette.grey[700],
		padding: theme.spacing(2),
		marginTop: theme.spacing(2)
	},
	textBlockLabel: {
		fontWeight: 'bold',
		marginBottom: theme.spacing(0.5)
	},
	categoryListTextBlock: {
		marginTop: theme.spacing(2)
	}
}));

interface PublishDialogProps {
	dialogOpenState: boolean;
	onCloseDialog: () => void;
	articleTitle: string;
	onPublish: (isSendEmails: boolean) => void;
	articleState?: ArticleState;
	wasPublishedPreviously: boolean;
	isCustomPublishDate: boolean;
	numberOfSubscribers?: number;
	categoriesAsString?: string;
}

const PublishDialog: React.FC<PublishDialogProps> = (
	{
		dialogOpenState,
		onCloseDialog,
		articleTitle,
		onPublish,
		articleState,
		wasPublishedPreviously,
		isCustomPublishDate,
		numberOfSubscribers = 0,
		categoriesAsString = ''
	}
) => {
	const classes = usePublishDialogStyles();
	const [isSendEmails, setIsSendEmails] = useState<boolean>(false);

	const publishOrUpdate = articleState === ArticleState.Published ? 'Update' : 'Publish';
	const showCheckbox = articleState === ArticleState.Published || wasPublishedPreviously || isCustomPublishDate;
	const checkboxLabel = `Notify
	${numberOfSubscribers > 1 ? `${numberOfSubscribers} subscribers` : `${numberOfSubscribers} subscriber`}`;
	const labelType = getDialogType(publishOrUpdate, numberOfSubscribers, isCustomPublishDate);
	const labels = getLabelAccordingToDialogType(labelType);

	const body = (
		<div>
			<TextBlock styleName="body" color="grey" shade={200} >
				You are about to {publishOrUpdate.toLowerCase()} the article <strong>{articleTitle}</strong>.
				{labels.notification}
				{labels.confirmation}
			</TextBlock>
			<div>
				{
					(publishOrUpdate === 'Publish' && !isCustomPublishDate && numberOfSubscribers > 0) && (
						<div className={classes.numberOfCategorySubscriptionsBlock}>
							<TextBlock
								className={classes.textBlockLabel}
								styleName="caption1"
								color="grey"
								shade={400}
							>
								Number of category subscribers
							</TextBlock>
							<TextBlock styleName="largeBody1">
								{`${numberOfSubscribers} ${numberOfSubscribers > 1 ? 'users' : 'user'}`}
							</TextBlock>
						</div>
					)
				}
			</div>
			{
				(showCheckbox && numberOfSubscribers > 0) && (
					<div className={classes.dialogCheckbox}>
						<Checkbox
							id="notify-subscribers-checkbox"
							label={checkboxLabel}
							value="a"
							checked={isSendEmails}
							onChange={e => setIsSendEmails(e?.target?.checked)} />
					</div>
				)
			}
			{
				publishOrUpdate === 'Publish' && (
					<TextBlock className={classes.categoryListTextBlock} styleName="body">
						This article will be published under <strong>{categoriesAsString}</strong>.
					</TextBlock>
				)
			}
		</div>
	);

	return (
		<ManageTemplateDialog
			title={publishOrUpdate + ' this article?'}
			submitBtnText={publishOrUpdate + ' article'}
			dialogOpenState={dialogOpenState}
			onDelete={() => onPublish(!showCheckbox || isSendEmails)}
			submitBtnColorType="primary"
			closeDialog={onCloseDialog}
		>
			{body}
		</ManageTemplateDialog>
	);
};

export default ArticleAddEdit;
